/**
 * Quick View — opens a product modal from any product card.
 * Triggers: [data-quick-view] (with data-product-handle), or the
 * "+N more" swatch overflow. Fetches /products/{handle}.js on open
 * (no per-card payload needed) and renders media + variant pickers + ATC.
 */
(function () {
  'use strict';

  var dom = null;
  var cache = {};
  var inflight = {};
  var state = null;

  function ic(n) { return '<i class="ph-bold ' + n + '"></i>'; }
  // Normalize injected Phosphor icons to the theme's configured weight class
  // (the hardcoded ph-bold above only renders if the bold weight stylesheet is
  // loaded; UntamedIcons.refresh adds the active weight class so glyphs show).
  function paintIcons() {
    if (window.UntamedIcons && dom) window.UntamedIcons.refresh(dom.sheet);
  }
  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"]/g, function (c) {
      return ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c];
    });
  }
  function money(cents) {
    if (window.formatMoney && window.themeMoneyFormat) {
      try { return window.formatMoney(cents, window.themeMoneyFormat); } catch (e) {}
    }
    return '$' + (cents / 100).toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,');
  }
  function isColor(name) { return /colou?r/i.test(name || ''); }
  function swatchColor(name) {
    // Prefer the global, brand-tuned color map shared with the product page.
    var ucs = window.UntamedColorSwatches;
    if (ucs && typeof ucs.resolve === 'function') {
      var hex = ucs.resolve(name);
      if (hex) return hex;
    }
    // Last-resort literal CSS color fall-through; the global hydrator will
    // back-fill anything else via api.color.pizza using data-color-name.
    var n = String(name).trim().toLowerCase().replace(/\s+/g, '');
    return /^[a-z]+$/.test(n) ? n : '';
  }

  // Build the shell ONCE using the shared ui-sheet markup. Open/close, overlay,
  // Esc, focus and animation are all handled by ui-sheet.js (window.UISheet).
  function build() {
    if (dom) return dom;
    var scrim = document.createElement('div');
    scrim.className = 'ui-sheet-overlay';
    scrim.setAttribute('data-ui-sheet-overlay', 'quick-view');
    scrim.setAttribute('aria-hidden', 'true');
    var sheet = document.createElement('div');
    sheet.className = 'ui-sheet ui-sheet--quickview';
    sheet.setAttribute('data-ui-sheet', 'quick-view');
    sheet.setAttribute('role', 'dialog');
    sheet.setAttribute('aria-modal', 'true');
    sheet.setAttribute('aria-hidden', 'true');
    sheet.innerHTML =
      '<div class="ui-sheet__header">' +
        '<h3 class="ui-sheet__title" data-ui-sheet-title>Quick view</h3>' +
        '<button type="button" class="ui-sheet__close" data-ui-sheet-close aria-label="Close">' + ic('ph-x') + '</button>' +
      '</div>' +
      '<div class="ui-sheet__body" data-qv-root></div>';
    document.body.appendChild(scrim);
    document.body.appendChild(sheet);
    dom = { scrim: scrim, sheet: sheet, body: sheet.querySelector('[data-qv-root]') };

    // emit close analytics when ui-sheet closes this sheet
    document.addEventListener('ui-sheet:close', function (e) {
      if (e.detail && e.detail.id === 'quick-view' && state) {
        emit('quick_view_closed', { product_handle: state.product.handle });
        state = null;
      }
    });
    return dom;
  }

  function open(handle, source) {
    build();
    emit('quick_view_opened', { product_handle: handle, source: source || 'card_tap' });
    if (cache[handle]) {
      render(cache[handle]);                 // instant — already prefetched
    } else {
      dom.body.innerHTML = skeleton();
      fetchProduct(handle).then(render).catch(function () {
        dom.body.innerHTML = '<div class="qv-loading">Couldn\'t load this item.<br><a class="ui-sheet__btn ui-sheet__btn--secondary qv-view-full" href="/products/' + esc(handle) + '">View full details</a></div>';
      });
    }
    if (window.UISheet) window.UISheet.open('quick-view');
    else { dom.scrim.setAttribute('aria-hidden', 'false'); dom.sheet.setAttribute('aria-hidden', 'false'); }
  }

  function close() {
    if (window.UISheet) window.UISheet.close();
    else if (dom) { dom.scrim.setAttribute('aria-hidden', 'true'); dom.sheet.setAttribute('aria-hidden', 'true'); }
  }

  function fetchProduct(handle) {
    if (cache[handle]) return Promise.resolve(cache[handle]);
    if (inflight[handle]) return inflight[handle];
    inflight[handle] = fetch('/products/' + handle + '.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { if (!r.ok) throw new Error('fetch'); return r.json(); })
      .then(function (p) { cache[handle] = p; delete inflight[handle]; return p; })
      .catch(function (e) { delete inflight[handle]; throw e; });
    return inflight[handle];
  }

  // Prefetch on intent (hover / focus / touch) so the sheet opens instantly.
  function prefetch(handle) {
    if (!handle || cache[handle] || inflight[handle]) return;
    fetchProduct(handle).catch(function () {});
  }

  // Loading skeleton — mirrors the real layout (media block + content lines)
  // so the sheet has structure the instant it opens, then crossfades to content.
  function skeleton() {
    return '<div class="qv-skeleton" data-qv-skeleton>' +
      '<div class="qv-skeleton__media sk-shimmer"></div>' +
      '<div class="qv-skeleton__content">' +
        '<span class="sk-line sk-shimmer" style="width:32%;height:11px"></span>' +
        '<span class="sk-line sk-shimmer" style="width:78%;height:24px;margin-top:10px"></span>' +
        '<span class="sk-line sk-shimmer" style="width:40%;height:22px;margin-top:16px"></span>' +
        '<div class="qv-skeleton__row">' +
          '<span class="sk-chip sk-shimmer"></span><span class="sk-chip sk-shimmer"></span><span class="sk-chip sk-shimmer"></span>' +
        '</div>' +
        '<div class="qv-skeleton__row">' +
          '<span class="sk-dot sk-shimmer"></span><span class="sk-dot sk-shimmer"></span><span class="sk-dot sk-shimmer"></span>' +
        '</div>' +
        '<span class="sk-line sk-shimmer" style="width:100%;height:52px;margin-top:auto;border-radius:100px"></span>' +
      '</div>' +
    '</div>';
  }

  function emit(name, detail) {
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(Object.assign({ event: name }, detail || {}));
      if (window.Shopify && window.Shopify.analytics && Shopify.analytics.publish) Shopify.analytics.publish(name, detail || {});
    } catch (e) {}
  }

  function render(p) {
    // initial chosen options = first available variant
    var firstAvail = p.variants.filter(function (v) { return v.available; })[0] || p.variants[0];
    var chosen = (firstAvail.options || []).slice();
    state = { product: p, chosen: chosen, mediaIndex: 0, qty: 1 };

    var media = (p.media && p.media.length) ? p.media : (p.images || []).map(function (src) { return { src: src, media_type: 'image' }; });

    // The product name lives in the body (.qv-title); the header keeps the
    // short, static "Quick view" label so the title isn't duplicated.
    dom.body.innerHTML =
      '<div class="qv-media" data-qv-media></div>' +
      '<div class="qv-content" data-qv-body></div>';

    renderMedia(media);
    renderBody();
    paintIcons();
    revealContent();
  }

  // GSAP entrance — stagger the media + content rows in. Falls back to a CSS
  // fade if GSAP isn't ready yet.
  function revealContent() {
    var mediaEl = dom.body.querySelector('[data-qv-media]');
    var contentEls = dom.body.querySelectorAll('.qv-content > *');
    function run(gsap) {
      if (gsap) {
        gsap.fromTo(mediaEl, { autoAlpha: 0, scale: 1.02 }, { autoAlpha: 1, scale: 1, duration: 0.5, ease: 'power2.out' });
        gsap.fromTo(contentEls, { autoAlpha: 0, y: 12 }, { autoAlpha: 1, y: 0, duration: 0.45, ease: 'power2.out', stagger: 0.05, delay: 0.05 });
      }
    }
    if (window.gsap) run(window.gsap);
    else if (window.gsapReady && window.gsapReady.then) window.gsapReady.then(run).catch(function () {});
  }

  function currentVariant() {
    var p = state.product;
    for (var i = 0; i < p.variants.length; i++) {
      var v = p.variants[i], ok = true;
      for (var k = 0; k < state.chosen.length; k++) {
        if (String(v.options[k]) !== String(state.chosen[k])) { ok = false; break; }
      }
      if (ok) return v;
    }
    return null;
  }

  function valueAvailable(idx, val) {
    var p = state.product;
    for (var i = 0; i < p.variants.length; i++) {
      var v = p.variants[i];
      if (!v.available) continue;
      if (String(v.options[idx]) !== String(val)) continue;
      var ok = true;
      for (var k = 0; k < p.options.length; k++) {
        if (k === idx) continue;
        if (String(v.options[k]) !== String(state.chosen[k])) { ok = false; break; }
      }
      if (ok) return true;
    }
    return false;
  }

  function renderMedia(media) {
    var wrap = dom.body.querySelector('[data-qv-media]');
    if (!media.length) { wrap.innerHTML = ''; return; }
    var i = state.mediaIndex;
    var m = media[i];
    var mainSrc = m.src || (m.preview_image && m.preview_image.src) || m;
    var isVid = m.media_type === 'video';
    wrap.innerHTML =
      '<div class="qv-media-main">' +
        '<span class="qv-media-counter">' + (i + 1) + ' / ' + media.length + '</span>' +
        (isVid ? '<video src="' + esc((m.sources && m.sources[0] && m.sources[0].url) || mainSrc) + '" muted playsinline loop autoplay></video>'
               : '<img src="' + esc(mainSrc) + '" alt="">') +
        (media.length > 1 ? '<button class="qv-media-arrow prev" data-qv-prev ' + (i === 0 ? 'disabled' : '') + '>' + ic('ph-caret-left') + '</button>' +
          '<button class="qv-media-arrow next" data-qv-next ' + (i === media.length - 1 ? 'disabled' : '') + '>' + ic('ph-caret-right') + '</button>' : '') +
      '</div>' +
      (media.length > 1 && (!window.UntamedSheets || !window.UntamedSheets.qv || window.UntamedSheets.qv.showThumbnails !== false) ? '<div class="qv-thumbs">' + media.map(function (mm, idx) {
        var t = (mm.preview_image && mm.preview_image.src) || mm.src || mm;
        return '<button class="qv-thumb' + (idx === i ? ' is-active' : '') + '" data-qv-thumb="' + idx + '"><img src="' + esc(t) + '" alt=""></button>';
      }).join('') + '</div>' : '');

    var prev = wrap.querySelector('[data-qv-prev]');
    var next = wrap.querySelector('[data-qv-next]');
    if (prev) prev.addEventListener('click', function () { if (state.mediaIndex > 0) { state.mediaIndex--; renderMedia(media); } });
    if (next) next.addEventListener('click', function () { if (state.mediaIndex < media.length - 1) { state.mediaIndex++; renderMedia(media); } });
    wrap.querySelectorAll('[data-qv-thumb]').forEach(function (t) {
      t.addEventListener('click', function () { state.mediaIndex = parseInt(t.getAttribute('data-qv-thumb'), 10); renderMedia(media); });
    });
    state._media = media;
    paintIcons();
  }

  function syncMediaToVariant() {
    var v = currentVariant();
    if (!v || v.featured_image == null || !state._media) return;
    var fid = v.featured_image.id;
    for (var i = 0; i < state._media.length; i++) {
      if (state._media[i].id === fid) { state.mediaIndex = i; renderMedia(state._media); break; }
    }
  }

  function renderBody() {
    var p = state.product;
    var v = currentVariant();
    var onSale = v && v.compare_at_price > v.price;

    var pickerOpts = (p.options || []).map(function (opt, idx) {
      if (p.options.length === 1 && opt.name === 'Title' && (opt.values || []).length === 1 && opt.values[0] === 'Default Title') return null;
      var color = isColor(opt.name);
      return {
        name: opt.name,
        oi: idx,
        isColor: color,
        selected: color ? state.chosen[idx] : null,
        values: opt.values.map(function (val) {
          return { value: val, selected: String(state.chosen[idx]) === String(val), available: valueAvailable(idx, val), hex: color ? swatchColor(val) : null };
        })
      };
    }).filter(Boolean);
    var pickers = pickerOpts.length ? window.UntamedSheets.renderVariantPicker([{ pi: 0, options: pickerOpts }], { bare: true }) : '';

    var QV = (window.UntamedSheets && window.UntamedSheets.qv) || {};
    var bodyHtml =
      (QV.showVendor && p.vendor ? '<div class="qv-eyebrow">' + esc(p.vendor) + '</div>' : '') +
      (QV.showType !== false && p.type ? '<div class="qv-eyebrow">' + esc(p.type) + '</div>' : '') +
      '<h2 class="qv-title">' + esc(p.title) + '</h2>' +
      '<div class="qv-price" data-qv-price></div>' +
      (pickers ? '<div class="qv-pickers">' + pickers + '</div>' : '') +
      '<div class="qv-foot">' +
        '<div class="qv-atc-row">' +
          '<div class="qv-qty"><button type="button" data-qv-qty="-1" aria-label="Decrease">' + ic('ph-minus') + '</button><span class="v" data-qv-qtyval>1</span><button type="button" data-qv-qty="1" aria-label="Increase">' + ic('ph-plus') + '</button></div>' +
          '<button class="ui-sheet__btn ui-sheet__btn--primary qv-atc" data-qv-atc>' + esc(QV.atc || 'Add to cart') + '</button>' +
        '</div>' +
        '<a class="ui-sheet__btn ui-sheet__btn--secondary ui-sheet__btn--block qv-view-full" href="' + esc(p.url) + '">' + esc(QV.viewFull || 'View full details') + ' ' + ic('ph-arrow-right') + '</a>' +
        '<div class="qv-error" data-qv-error hidden></div>' +
      '</div>';

    dom.body.querySelector('[data-qv-body]').innerHTML = bodyHtml;
    paintPrice();
    bindBody();
    paintIcons();
  }

  function paintPrice() {
    var v = currentVariant();
    var el = dom.body.querySelector('[data-qv-price]');
    if (!el) return;
    if (!v) { el.innerHTML = '<span class="now">—</span>'; return; }
    var onSale = v.compare_at_price > v.price;
    el.innerHTML = '<span class="now">' + money(v.price) + '</span>' +
      (onSale ? '<span class="was">' + money(v.compare_at_price) + '</span><span class="save">Save ' + money(v.compare_at_price - v.price) + '</span>' : '');
    var atc = dom.body.querySelector('[data-qv-atc]');
    if (atc) {
      if (!v.available) { atc.textContent = 'Sold out'; atc.disabled = true; }
      else if (!atc.classList.contains('is-added')) { atc.textContent = 'Add to cart'; atc.disabled = false; }
    }
  }

  function bindBody() {
    dom.body.querySelectorAll('[data-vp-val]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (btn.getAttribute('aria-disabled') === 'true') return;
        var idx = parseInt(btn.getAttribute('data-vp-oi'), 10);
        state.chosen[idx] = btn.getAttribute('data-vp-val');
        emit('quick_view_variant_changed', { product_handle: state.product.handle });
        renderBody();
        syncMediaToVariant();
      });
    });
    dom.body.querySelectorAll('[data-qv-qty]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var d = parseInt(btn.getAttribute('data-qv-qty'), 10);
        state.qty = Math.max(1, state.qty + d);
        var v = dom.body.querySelector('[data-qv-qtyval]'); if (v) v.textContent = state.qty;
      });
    });
    var atc = dom.body.querySelector('[data-qv-atc]');
    if (atc) atc.addEventListener('click', addToCart);
  }

  function addToCart() {
    var v = currentVariant();
    var atc = dom.body.querySelector('[data-qv-atc]');
    var err = dom.body.querySelector('[data-qv-error]');
    if (err) err.hidden = true;
    if (!v) { if (err) { err.hidden = false; err.textContent = 'Please choose an option.'; } return; }
    if (!window.CartAPI || !window.CartAPI.addItem) {
      if (err) { err.hidden = false; err.textContent = 'Cart is unavailable. Please try again.'; }
      return;
    }
    atc.disabled = true; atc.textContent = 'Adding…';
    // Use the theme's Cart API. It performs the add and dispatches
    // cart:item-added / cart:updated, which the cart drawer listens for
    // (the drawer opens itself per its open-on-add setting). We just close
    // the quick-view sheet on success — no bespoke fetch/event logic here.
    window.CartAPI.addItem(v.id, state.qty).then(function () {
      emit('quick_view_added_to_cart', { product_handle: state.product.handle, variant_id: v.id, quantity: state.qty, price: v.price });
      atc.disabled = false; atc.textContent = 'Add to cart';
      paintPrice();
      close();
    }).catch(function () {
      atc.disabled = false; atc.textContent = 'Add to cart';
      if (err) { err.hidden = false; err.textContent = 'Could not add to cart. Please try again.'; }
    });
  }

  // Delegated trigger — explicit "Quick view" pill/button
  document.addEventListener('click', function (e) {
    var t = e.target.closest('[data-quick-view]');
    if (t) {
      var handle = t.getAttribute('data-product-handle');
      if (!handle) return;
      e.preventDefault();
      e.stopPropagation();
      open(handle, 'quick_view_button');
      return;
    }
    // Whole-card tap opens quick view (when enabled), instead of the PDP.
    var card = e.target.closest('[data-quick-view-card]');
    if (!card) return;
    // Let real controls behave normally: wishlist, swatches, add buttons,
    // form fields, and anything explicitly opted out.
    if (e.target.closest('[data-wishlist-btn], .product-card__color-swatches, .product-card__color-swatch, .product-card__actions, button, select, input, [data-no-quick-view]')) return;
    var h = card.getAttribute('data-product-handle');
    if (!h) return;
    e.preventDefault();
    open(h, 'card_tap');
  });
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    var t = e.target.closest && e.target.closest('[data-quick-view]');
    if (!t) return;
    e.preventDefault();
    open(t.getAttribute('data-product-handle'), 'quick_view_button');
  });

  // Prefetch product JSON on intent so the sheet opens instantly.
  function intentPrefetch(e) {
    var el = e.target.closest && e.target.closest('[data-quick-view-card],[data-quick-view]');
    if (!el) return;
    prefetch(el.getAttribute('data-product-handle'));
  }
  document.addEventListener('pointerover', intentPrefetch, { passive: true });
  document.addEventListener('focusin', intentPrefetch);
  document.addEventListener('touchstart', intentPrefetch, { passive: true });

  window.QuickView = { open: open, close: close };
})();
